<template>
  <component :is="componentType" v-bind="$attrs" :class="rootClass" v-on="$listeners">
    <SpinnerIcon v-if="loading" :height="iconSize" :width="iconSize" class="bravado-button-v3__spinner" />
    <span v-else class="bravado-button-v3__content">
      <template>
        <span v-if="$scopedSlots['prepend-icon']" class="bravado-button-v3__icon bravado-button-v3__icon--prepend">
          <slot name="prepend-icon" :icon-size="iconSize" />
        </span>
        <span v-if="$scopedSlots['icon']" class="bravado-button-v3__icon">
          <slot name="icon" :icon-size="iconSize" />
        </span>
        <slot />
        <span v-if="$scopedSlots['append-icon']" class="bravado-button-v3__icon bravado-button-v3__icon--append">
          <slot name="append-icon" :icon-size="iconSize" />
        </span>
      </template>
    </span>
  </component>
</template>

<script lang="ts" setup>
import { computed, useAttrs } from '@nuxtjs/composition-api';
import { BravadoButtonV3Size } from './types';
import SpinnerIcon from '~/components/common/BravadoButtonV3/images/spinner.vue.svg';

type BravadoButtonV3Type = 'a' | 'button' | 'NuxtLink';
type BravadoButtonV3Variant = 'accent' | 'primary' | 'secondary' | 'danger' | 'blank' | 'border';
type BravadoButtonV3Props = {
  size?: BravadoButtonV3Size;
  variant?: BravadoButtonV3Variant;
  isIconButton?: boolean;
  isSoftDisabled?: boolean;
  elevated?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
};

const props = withDefaults(defineProps<BravadoButtonV3Props>(), {
  size: BravadoButtonV3Size.md,
  variant: 'accent',
  isIconButton: false,
  isSoftDisabled: false,
  elevated: false,
  loading: false,
  fullWidth: false,
});

const componentType = computed<BravadoButtonV3Type>(() => {
  const { href, to, disabled } = useAttrs();

  if (href && !disabled) return 'a';
  if (to && !disabled) return 'NuxtLink';
  return 'button';
});

const rootClass = computed(() => {
  return {
    'bravado-button-v3': true,
    [`bravado-button-v3--${props.size}`]: true,
    [`bravado-button-v3--${props.variant}`]: true,
    'bravado-button-v3--icon': props.isIconButton,
    'bravado-button-v3--soft-disabled': props.isSoftDisabled,
    'bravado-button-v3--elevated': props.elevated,
    'bravado-button-v3--loading': props.loading,
    'bravado-button-v3--full-width': props.fullWidth,
  };
});

const iconSize = computed(() => {
  if (props.size === BravadoButtonV3Size.xs) return 16;
  if (props.size === BravadoButtonV3Size.sm) return 20;
  return 24;
});
</script>

<style scoped lang="scss">
.bravado-button-v3 {
  $this: &;
  $softDisabled: #{$this}--soft-disabled;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  appearance: none;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition-timing-function: ease-in-out;
  transition-duration: 0.2s;
  transition-property: color, background-color, border-color, box-shadow;
  padding: 0;
  box-sizing: border-box;
  border-radius: 4px;
  outline: 2px solid transparent;
  outline-offset: 2px;

  &__content {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
  }

  &:disabled > &__content {
    opacity: 0.3;
  }

  &:disabled,
  &#{$softDisabled} {
    cursor: no-drop;
  }

  &:focus-visible {
    &:not([disabled], #{$softDisabled}) {
      outline-color: var(--blue-60);
    }
  }

  &__icon {
    line-height: 0;
  }

  &--full-width {
    width: 100%;
  }

  &--loading {
    @keyframes spinner {
      0% {
        transform: rotate(0deg);
      }

      100% {
        transform: rotate(360deg);
      }
    }

    pointer-events: none;

    #{$this}__spinner {
      animation: 1.5s linear infinite spinner;
    }
  }

  &--elevated {
    box-shadow: var(--theme-elevation-10);

    &:hover,
    &:focus-visible {
      &:not([disabled], #{$softDisabled}) {
        box-shadow: var(--theme-elevation-15);
      }
    }
  }

  // Size: START
  &--xs {
    @include subhead-semibold;

    padding: 5px;

    &:not(#{$this}--icon) {
      padding: 5px 6px;
    }

    #{$this}__icon {
      &--prepend {
        margin-right: 2px;
      }

      &--append {
        margin-left: 2px;
      }
    }
  }

  &--sm {
    @include subhead-semibold;

    padding: 8px;

    &:not(#{$this}--icon) {
      padding: 8px 12px;
    }

    #{$this}__icon {
      &--prepend {
        margin-right: 4px;
      }

      &--append {
        margin-left: 4px;
      }
    }
  }

  &--md {
    @include body-semibold;

    padding: 12px;

    &:not(#{$this}--icon) {
      padding: 12px 16px;
    }

    #{$this}__icon {
      &--prepend {
        margin-right: 8px;
      }

      &--append {
        margin-left: 8px;
      }
    }
  }

  &--lg {
    @include headline-semibold;

    padding: 8px;

    &:not(#{$this}--icon) {
      padding: 16px 20px;
    }

    #{$this}__icon {
      &--prepend {
        margin-right: 12px;
      }

      &--append {
        margin-left: 12px;
      }
    }
  }

  // Size: FINISH

  // Variant: START
  &--accent {
    background-color: var(--blue-60);
    color: rgba(var(--white));

    &:hover,
    &:focus-visible {
      &:not([disabled], #{$softDisabled}) {
        background-color: var(--blue-50);
      }
    }
  }

  &--primary {
    background-color: var(--theme-color-text);
    color: var(--theme-color-card);

    &:hover,
    &:focus-visible {
      &:not([disabled], #{$softDisabled}) {
        background-color: var(--theme-color-text-hover);
      }
    }
  }

  &--secondary {
    background-color: var(--theme-color-over-card);
    color: var(--theme-color-text);

    &:hover,
    &:focus-visible {
      &:not([disabled], #{$softDisabled}) {
        background-color: var(--theme-color-over-card-hover);
      }
    }
  }

  &--danger {
    background-color: var(--red-50);
    color: rgba(var(--white));

    &:hover,
    &:focus-visible {
      &:not([disabled], #{$softDisabled}) {
        background-color: var(--red-50);
      }
    }
  }

  &--border {
    background-color: transparent;
    color: var(--theme-color-text);
    border: 1px solid var(--gray-50-a30);

    &:hover,
    &:focus-visible {
      &:not([disabled], #{$softDisabled}) {
        background-color: var(--gray-50-a20);
      }
    }
  }

  &--blank {
    background-color: transparent;
    color: var(--gray-50);

    &:hover,
    &:focus-visible {
      &:not([disabled], #{$softDisabled}) {
        background-color: var(--theme-color-over-card);
      }
    }
  }

  // Variant: FINISH
}
</style>
